import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { ArrowLeft, ExternalLink, GitBranch, Clock } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { clsx } from 'clsx';
import PageHeader from '@/components/common/PageHeader';
import StatusBadge from '@/components/common/StatusBadge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorBanner from '@/components/common/ErrorBanner';
import DataTable, { type Column } from '@/components/common/DataTable';
import MetricCard from '@/components/common/MetricCard';
import { useService, useServiceHealth, useServiceCostByEnv } from '@/hooks/useServices';
import { useDeployments } from '@/hooks/useDeployments';
import { useServiceObservability } from '@/hooks/useObservability';
import type { Deployment, Monitor } from '@/types';

type TabId = 'overview' | 'builds' | 'deployments' | 'observability' | 'infrastructure' | 'costs';

const tabs: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'deployments', label: 'Deployments' },
  { id: 'observability', label: 'Observability' },
  { id: 'costs', label: 'Costs' },
];

const PROVIDER_COLORS: Record<string, string> = {
  aws: '#FF9900',
  azure: '#0078D4',
  gcp: '#4285F4',
  kubernetes: '#326CE5',
};

const PROVIDER_LABELS: Record<string, string> = {
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP',
  kubernetes: 'K8s',
};

function formatUsd(n: number | null | undefined): string {
  const v = n ?? 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
}

export default function ServiceDetail() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const { data: service, isLoading, error, refetch } = useService(slug);
  const { data: health } = useServiceHealth(slug);

  if (isLoading) {
    return <div className="py-20"><LoadingSpinner message="Loading service..." /></div>;
  }

  if (error || !service) {
    return <ErrorBanner title="Service not found" message="Unable to load the service details." onRetry={refetch} />;
  }

  return (
    <div className="animate-fade-in">
      <Link
        to="/services"
        className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
      >
        <ArrowLeft className="h-4 w-4" /> Back to catalog
      </Link>

      <PageHeader
        title={service.name}
        description={service.description ?? `${service.service_type} service`}
        actions={
          service.github_repo_name ? (
            <a
              href={`https://github.com/${service.github_repo_name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200/60 dark:border-slate-800/80 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 transition-colors hover:bg-surface-50 dark:hover:bg-slate-800"
            >
              <GitBranch className="h-4 w-4" />
              {service.github_repo_name}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : undefined
        }
      />

      {/* Health summary */}
      {health && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MetricCard label="Build Status" value={health.build_status ?? 'unknown'} />
          <MetricCard label="Deploy Status" value={health.deploy_status ?? 'unknown'} />
          <MetricCard label="Monitor Status" value={health.monitor_status ?? 'unknown'} />
          <MetricCard label="Active Alerts" value={health.active_alerts_count} />
        </div>
      )}

      {/* Tabs – pill style */}
      <div className="mb-6">
        <nav className="inline-flex gap-1 rounded-lg bg-surface-100 dark:bg-slate-800 p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'rounded-lg px-4 py-2 text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-white dark:bg-slate-900 shadow-sm text-brand-700 dark:text-brand-400'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300',
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <OverviewTab service={service} />}
      {activeTab === 'deployments' && <DeploymentsTab serviceId={service.id} />}
      {activeTab === 'observability' && <ObservabilityTab serviceSlug={service.slug} />}
      {activeTab === 'costs' && <CostsTab serviceSlug={service.slug} />}
    </div>
  );
}

function CostsTab({ serviceSlug }: { serviceSlug: string }) {
  const [periodDays, setPeriodDays] = useState(30);
  const { data, isLoading, error, refetch } = useServiceCostByEnv(serviceSlug, periodDays);

  if (isLoading) return <LoadingSpinner message="Loading cost data..." />;
  if (error) return <ErrorBanner message="Failed to load cost data" onRetry={refetch} />;
  if (!data) return null;

  const envs = data.envs ?? [];

  return (
    <div className="animate-slide-up space-y-6">
      {/* Header: period selector + total */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-section-title text-slate-900 dark:text-slate-100">
            Cost by environment
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Period: {data.period_start} → {data.period_end} ·{' '}
            <span className="font-medium text-slate-700 dark:text-slate-300">{formatUsd(data.total_cost)} total</span>
          </p>
        </div>
        <select
          value={periodDays}
          onChange={(e) => setPeriodDays(Number(e.target.value))}
          className="select-field"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {envs.length === 0 ? (
        <div className="card card-body text-center text-sm text-slate-500 dark:text-slate-400">
          No cost data for this service in the selected period.<br />
          {' '}
          <span className="text-xs">
            (Tip: this service's `cloud_accounts` need to match the account
            IDs Vantage reports for cost to flow through.)
          </span>
        </div>
      ) : (
        <>
          {/* Per-env metric cards */}
          <div
            className={clsx(
              'grid gap-4',
              envs.length === 1 && 'grid-cols-1',
              envs.length === 2 && 'grid-cols-2',
              envs.length >= 3 && 'grid-cols-2 lg:grid-cols-3',
            )}
          >
            {envs.map((env) => (
              <div
                key={env.environment}
                className="rounded-2xl border border-slate-200/60 dark:border-slate-800/80 bg-white dark:bg-slate-900 shadow-card p-5 transition-shadow hover:shadow-card-hover hover:border-slate-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 capitalize">
                    {env.environment}
                  </span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {((env.total_cost / Math.max(data.total_cost, 1)) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
                  {formatUsd(env.total_cost)}
                </div>
                {Object.keys(env.by_provider).length > 0 && (
                  <div className="mt-4 space-y-1.5">
                    {Object.entries(env.by_provider)
                      .sort((a, b) => b[1] - a[1])
                      .map(([provider, cost]) => (
                        <div key={provider} className="flex items-center gap-2 text-xs">
                          <span
                            className="inline-block h-2 w-2 rounded-full"
                            style={{
                              backgroundColor: PROVIDER_COLORS[provider] ?? '#94a3b8',
                            }}
                          />
                          <span className="uppercase text-slate-600 dark:text-slate-400 w-12">{provider}</span>
                          <span className="text-slate-500 dark:text-slate-400 tabular-nums">{formatUsd(cost)}</span>
                          <span className="text-slate-400 dark:text-slate-500 ml-auto tabular-nums">
                            {((cost / Math.max(env.total_cost, 1)) * 100).toFixed(0)}%
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Detailed table */}
          <div className="card overflow-hidden">
            <div className="card-header">
              <h4 className="text-card-title text-slate-700 dark:text-slate-300">Breakdown</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-2 font-medium">Environment</th>
                    <th className="px-4 py-2 font-medium text-right">AWS</th>
                    <th className="px-4 py-2 font-medium text-right">Azure</th>
                    <th className="px-4 py-2 font-medium text-right">GCP</th>
                    <th className="px-4 py-2 font-medium text-right">K8s</th>
                    <th className="px-4 py-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                  {envs.map((env) => (
                    <tr key={env.environment} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center rounded-md bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 capitalize">
                          {env.environment}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                        {formatUsd(env.by_provider.aws ?? 0)}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                        {formatUsd(env.by_provider.azure ?? 0)}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                        {formatUsd(env.by_provider.gcp ?? 0)}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                        {formatUsd(env.by_provider.kubernetes ?? 0)}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
                        {formatUsd(env.total_cost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function OverviewTab({ service }: { service: NonNullable<ReturnType<typeof useService>['data']> }) {
  return (
    <div className="animate-slide-up grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="card card-body space-y-4">
        <h3 className="text-section-title text-slate-900 dark:text-slate-100">Service Information</h3>
        <dl className="space-y-3">
          {[
            ['Type', service.service_type],
            ['Lifecycle', service.lifecycle_stage],
            ['Team', service.team_name ?? 'Unassigned'],
            ['Language', service.language ?? 'N/A'],
            ['Framework', service.framework ?? 'N/A'],
            ['Auto-discovered', service.is_auto_discovered ? 'Yes' : 'No'],
            ['Created', format(new Date(service.created_at), 'PPP')],
            ['Updated', formatDistanceToNow(new Date(service.updated_at), { addSuffix: true })],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between">
              <dt className="text-sm text-slate-500 dark:text-slate-400">{label}</dt>
              <dd className="text-sm font-medium text-slate-700 dark:text-slate-200">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="card card-body space-y-4">
        <h3 className="text-section-title text-slate-900 dark:text-slate-100">Tags & Links</h3>
        {service.tags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {service.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-brand-50 dark:bg-brand-950/40 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 dark:text-slate-500">No tags</p>
        )}
        {Object.keys(service.links).length > 0 ? (
          <ul className="space-y-2">
            {Object.entries(service.links).map(([label, url]) => (
              <li key={label}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                >
                  {label} <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400 dark:text-slate-500">No links</p>
        )}
      </div>
    </div>
  );
}

function DeploymentsTab({ serviceId }: { serviceId: string }) {
  const { data, isLoading } = useDeployments({ service_id: serviceId, page: 1, page_size: 20 });

  const columns: Column<Deployment>[] = [
    {
      key: 'version',
      header: 'Version',
      render: (row) => <span className="font-mono text-xs text-slate-700 dark:text-slate-300">{row.version ?? '-'}</span>,
    },
    {
      key: 'environment_name',
      header: 'Environment',
      render: (row) => <span className="text-slate-700 dark:text-slate-300">{row.environment_name ?? '-'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'triggered_by',
      header: 'Triggered By',
      render: (row) => <span className="text-slate-600 dark:text-slate-400">{row.triggered_by ?? '-'}</span>,
    },
    {
      key: 'duration_seconds',
      header: 'Duration',
      render: (row) =>
        row.duration_seconds ? (
          <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-400">
            <Clock className="h-3.5 w-3.5" />
            {Math.floor(row.duration_seconds / 60)}m {row.duration_seconds % 60}s
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">-</span>
        ),
    },
    {
      key: 'started_at',
      header: 'When',
      render: (row) => (
        <span className="text-slate-500 dark:text-slate-400">
          {formatDistanceToNow(new Date(row.started_at), { addSuffix: true })}
        </span>
      ),
    },
  ];

  if (isLoading) return <LoadingSpinner message="Loading deployments..." />;

  return (
    <div className="animate-slide-up card overflow-hidden">
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(row) => row.id}
        emptyTitle="No deployments"
        emptyDescription="No deployments found for this service."
      />
    </div>
  );
}

function ObservabilityTab({ serviceSlug }: { serviceSlug: string }) {
  const { data, isLoading, error, refetch } = useServiceObservability(serviceSlug);

  if (isLoading) return <LoadingSpinner message="Loading observability data..." />;
  if (error) return <ErrorBanner message="Failed to load observability data" onRetry={refetch} />;
  if (!data) return null;

  const monitorColumns: Column<Monitor>[] = [
    { key: 'name', header: 'Name', render: (row) => <span className="font-medium text-slate-900 dark:text-slate-100">{row.name}</span> },
    { key: 'type', header: 'Type', render: (row) => <span className="text-slate-600 dark:text-slate-400">{row.type ?? '-'}</span> },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
  ];

  return (
    <div className="animate-slide-up space-y-6">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Overall Status:</span>
        <StatusBadge status={data.overall_status} size="md" />
      </div>

      {/* Metric charts */}
      {Object.entries(data.metrics).length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Object.entries(data.metrics).map(([name, metric]) => (
            <div key={name} className="card card-body">
              <h4 className="mb-3 text-card-title text-slate-700 dark:text-slate-300">{metric.metric_name}</h4>
              {metric.data_points.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={metric.data_points}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => format(new Date(v), 'HH:mm')}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip
                      labelFormatter={(v) => format(new Date(v as string), 'PPp')}
                      formatter={(value: number) => [value.toFixed(2), metric.unit ?? 'value']}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">No data points</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Monitors */}
      <div className="card overflow-hidden">
        <div className="card-header">
          <h3 className="text-section-title text-slate-900 dark:text-slate-100">Monitors</h3>
        </div>
        <DataTable
          columns={monitorColumns}
          data={data.monitors}
          keyExtractor={(row) => row.id}
          emptyTitle="No monitors"
          emptyDescription="No Datadog monitors are linked to this service."
        />
      </div>
    </div>
  );
}
